import { useStationsGroups } from '@app/core/stations/hooks/useStationsGroupsQuery';
import {
  partitionStatusSections,
  SharedGroupStatusSection,
} from '@app/modules/logger/screens/status/helpers/partition-status-sections';
import { StationStatusListItem } from '@app/modules/logger/screens/status/types';
import { useMemo } from 'react';
import { useSortedStatuses } from './useSortedStatuses';

export interface StatusSectionsState {
  favorites: StationStatusListItem[];
  ownStations: StationStatusListItem[];
  individuallySharedStations: StationStatusListItem[];
  sharedGroupSections: SharedGroupStatusSection[];
  hasVisibleStations: boolean;
}

/**
 * Derives status-screen sections: favorites, own stations, individually shared stations,
 * and one section per shared group. Used by StatusLayout so sorting/grouping lives in one place.
 */
export function useStatusSections(statuses: StationStatusListItem[]): StatusSectionsState {
  const { favorites, nonFavorites } = useSortedStatuses(statuses);
  const stationsGroups = useStationsGroups();

  return useMemo(() => {
    const { ownStations, individuallySharedStations, sharedGroupSections } =
      partitionStatusSections(nonFavorites, stationsGroups);

    const hasVisibleStations =
      favorites.some((item) => item.isVisible) ||
      ownStations.some((item) => item.isVisible) ||
      individuallySharedStations.some((item) => item.isVisible) ||
      sharedGroupSections.some((section) => section.items.some((item) => item.isVisible));

    return {
      favorites,
      ownStations,
      individuallySharedStations,
      sharedGroupSections,
      hasVisibleStations,
    };
  }, [favorites, nonFavorites, stationsGroups]);
}
